import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Plus, Edit, Trash2, Save, X, RefreshCw, FileSpreadsheet, FileDown, Layers, Search, Percent, ChevronUp, ChevronDown, ChevronsUpDown, Copy } from 'lucide-react';
import { getPriceItems, addPriceItem, updatePriceItem, deletePriceItem, getPurchaseInvoices, mergeDuplicatePriceItems, bulkUpdatePriceItems, getSuppliers, addSupplier } from '../api';
import { toast } from '../hooks/use-toast';

const PriceList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkPercent, setBulkPercent] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [lowProfitThreshold, setLowProfitThreshold] = useState(() => {
    const saved = parseFloat(localStorage.getItem('lowProfitThreshold'));
    return isNaN(saved) ? 15 : saved;
  });
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [newRow, setNewRow] = useState({ partName: '', price: '', costPrice: '', supplier: '', invoiceId: '', date: '' });
  const [editItem, setEditItem] = useState({ partName: '', price: '', costPrice: '', supplier: '', invoiceId: '', date: '' });
  const [workId, setWorkId] = useState(null);
  const [workName, setWorkName] = useState('');
  
  // Use ref to preserve newRow during refresh operations - initialize empty
  const newRowRef = useRef({ partName: '', price: '', costPrice: '', supplier: '', invoiceId: '', date: '' });
  // Flag to prevent auto-save during refresh
  const isRefreshing = useRef(false);
  
  // Keep ref always in sync with state using useEffect
  useEffect(() => {
    newRowRef.current = newRow;
  }, [newRow]);

  // Persist low profit threshold so the Home page alert uses the same value
  useEffect(() => {
    localStorage.setItem('lowProfitThreshold', String(lowProfitThreshold));
  }, [lowProfitThreshold]);

  useEffect(() => {
    if (location.state?.workId) {
      setWorkId(location.state.workId);
      setWorkName(location.state.workName || '');
      loadItems(location.state.workId);
      loadInvoices();
      loadSuppliers();
    } else {
      navigate('/home');
    }
  }, [location]);

  const loadSuppliers = async () => {
    try {
      const data = await getSuppliers();
      setSuppliers(data || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const data = await getPurchaseInvoices();
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  // Reload when component comes back into view (but preserve newRow)
  useEffect(() => {
    const handleFocus = async () => {
      if (workId) {
        console.log('🔄 Window focused - reloading price list...');
        // Preserve the current newRow from ref
        const preservedNewRow = { ...newRowRef.current };
        
        try {
          const data = await getPriceItems(workId);
          console.log('📋 Focus reload - items:', data.length);
          setItems(data);
          
          // Restore newRow after data load
          setNewRow(preservedNewRow);
          newRowRef.current = preservedNewRow;
        } catch (error) {
          console.error('❌ Error reloading on focus:', error);
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [workId]);

  const loadItems = async (wId) => {
    try {
      const data = await getPriceItems(wId);
      console.log('📋 Price items loaded:', data.length);
      setItems(data);
    } catch (error) {
      console.error('❌ Error loading price items:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון את המחירון',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    if (workId) {
      // Note: isRefreshing.current is already set by onMouseDown
      
      // Store the current newRow value from ref BEFORE any async operation
      const preservedNewRow = { ...newRowRef.current };
      console.log('🔄 Preserving newRow before refresh:', JSON.stringify(preservedNewRow));
      
      try {
        const data = await getPriceItems(workId);
        console.log('🔄 Refreshed:', data.length, 'items. Restoring:', JSON.stringify(preservedNewRow));
        setItems(data);
        
        // Use functional update to ensure we set the preserved values
        setNewRow(() => preservedNewRow);
        newRowRef.current = preservedNewRow;
        
        toast({
          title: 'מחירון עודכן',
          description: `נטענו ${data.length} פריטים`
        });
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'לא ניתן לרענן',
          variant: 'destructive'
        });
      } finally {
        // Reset refreshing flag after a short delay
        setTimeout(() => {
          isRefreshing.current = false;
          console.log('🚩 Refresh flag reset');
        }, 300);
      }
    }
  }, [workId]);

  // Use functional update to avoid stale closure issues
  const handleNewRowChange = useCallback((field, value) => {
    setNewRow(prev => {
      const updatedRow = { ...prev, [field]: value };
      // Update ref synchronously
      newRowRef.current = updatedRow;
      return updatedRow;
    });
  }, []);

  const handleSaveNewRow = async () => {
    if (newRow.partName && newRow.price) {
      try {
        const added = await addPriceItem(workId, newRow);
        setItems([...items, added]);
        // Reset both state and ref
        const emptyRow = { partName: '', price: '', costPrice: '', supplier: '', invoiceId: '', date: '' };
        setNewRow(emptyRow);
        newRowRef.current = emptyRow;
        toast({
          title: 'נשמר',
          description: 'הפריט נוסף בהצלחה'
        });
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'לא ניתן להוסיף פריט',
          variant: 'destructive'
        });
      }
    }
  };

  const handleLastFieldBlur = () => {
    // Don't auto-save if we're refreshing
    if (isRefreshing.current) {
      console.log('🚫 Skipping auto-save during refresh');
      return;
    }
    setTimeout(() => {
      handleSaveNewRow();
    }, 100);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditItem({ partName: item.partName, price: item.price, costPrice: item.costPrice || '', supplier: item.supplier || '', invoiceId: item.invoiceId || '', date: item.date || '' });
  };

  const handleUpdate = async (id) => {
    if (!editItem.partName || !editItem.price) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא את כל השדות',
        variant: 'destructive'
      });
      return;
    }

    try {
      const updated = await updatePriceItem(id, { ...editItem, workId });
      setItems(items.map(item => item.id === id ? updated : item));
      setEditingId(null);
      toast({
        title: 'הצלחה',
        description: 'הפריט עודכן בהצלחה'
      });
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לעדכן פריט',
        variant: 'destructive'
      });
    }
  };

  const handleDuplicate = async (item) => {
    try {
      const copy = { partName: item.partName, price: item.price, costPrice: item.costPrice || '', supplier: item.supplier || '', invoiceId: item.invoiceId || '', date: item.date || '' };
      await addPriceItem(workId, copy);
      await loadItems(workId);
      toast({ title: 'פריט שוכפל', description: item.partName });
    } catch (e) {
      toast({ title: 'שגיאה', description: 'לא ניתן לשכפל פריט', variant: 'destructive' });
    }
  };


  const handleDelete = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק פריט זה?')) {
      try {
        await deletePriceItem(id);
        setItems(items.filter(item => item.id !== id));
        toast({
          title: 'הצלחה',
          description: 'הפריט נמחק בהצלחה'
        });
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'לא ניתן למחוק פריט',
          variant: 'destructive'
        });
      }
    }
  };

  // Set refreshing flag on mouse down (before blur event fires)
  const handleRefreshMouseDown = useCallback(() => {
    isRefreshing.current = true;
    console.log('🚩 Refresh flag set on mousedown');
  }, []);

  const handleMergeDuplicates = async () => {
    if (!workId) return;
    if (!window.confirm('לאחד פריטים כפולים או כמעט-זהים (אותו שם חלק, גם עם שינויי פיסוק/רווח)? הכפילויות יועברו לסל המחזור.')) return;
    try {
      const res = await mergeDuplicatePriceItems(workId);
      await loadItems(workId);
      toast({ title: 'אוחדו כפילויות', description: `הוסרו ${res.removed} פריטים כפולים` });
    } catch (e) {
      toast({ title: 'שגיאה', description: 'לא ניתן לאחד כפילויות', variant: 'destructive' });
    }
  };

  const isSelectedInvoice = (val) => invoices.some(inv => inv.id === val);

  const getItemSupplier = (item) => item.supplier || (invoices.find(inv => inv.id === item.invoiceId)?.supplierName) || '';

  const filteredItems = items.filter(item => {
    if (supplierFilter && getItemSupplier(item) !== supplierFilter) return false;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const inv = invoices.find(i => i.id === item.invoiceId);
    const invoiceStr = inv ? `${inv.invoiceNumber || ''} ${inv.supplierName || ''}` : (item.invoiceId || '');
    return (item.partName || '').toLowerCase().includes(q) ||
           (item.supplier || '').toLowerCase().includes(q) ||
           invoiceStr.toLowerCase().includes(q);
  });

  const profitPercent = (price, cost) => {
    const p = parseFloat(price) || 0;
    const c = parseFloat(cost) || 0;
    if (!c) return null;
    return ((p - c) / c) * 100;
  };

  const handleSort = (key) => {
    setSortConfig(prev => prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' });
  };

  const sortedItems = [...filteredItems];
  if (sortConfig.key) {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    sortedItems.sort((a, b) => {
      switch (sortConfig.key) {
        case 'partName':
          return (a.partName || '').localeCompare(b.partName || '', 'he') * dir;
        case 'supplier':
          return getItemSupplier(a).localeCompare(getItemSupplier(b), 'he') * dir;
        case 'costPrice':
          return ((parseFloat(a.costPrice) || 0) - (parseFloat(b.costPrice) || 0)) * dir;
        case 'price':
          return ((parseFloat(a.price) || 0) - (parseFloat(b.price) || 0)) * dir;
        case 'profit': {
          const ap = profitPercent(a.price, a.costPrice);
          const bp = profitPercent(b.price, b.costPrice);
          return (((ap == null ? -Infinity : ap)) - ((bp == null ? -Infinity : bp))) * dir;
        }
        default:
          return 0;
      }
    });
  }

  const SortIcon = ({ col }) => {
    if (sortConfig.key !== col) return <ChevronsUpDown className="inline w-3.5 h-3.5 text-gray-300 mr-1" />;
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="inline w-3.5 h-3.5 text-orange-600 mr-1" />
      : <ChevronDown className="inline w-3.5 h-3.5 text-orange-600 mr-1" />;
  };

  const handleBulkUpdate = async () => {
    const pct = parseFloat(bulkPercent);
    if (isNaN(pct)) {
      toast({ title: 'שגיאה', description: 'הזן אחוז תקין', variant: 'destructive' });
      return;
    }
    const ids = filteredItems.map(i => i.id);
    const scopeLabel = (supplierFilter || searchQuery.trim()) ? ` (${ids.length} מסוננים)` : '';
    if (!window.confirm(`לעדכן את מחירי הלקוח${scopeLabel} ב-${pct}%?`)) return;
    try {
      const res = await bulkUpdatePriceItems(workId, pct, ids);
      await loadItems(workId);
      setBulkPercent('');
      toast({ title: 'מחירים עודכנו', description: `עודכנו ${res.updated} פריטים ב-${pct}%` });
    } catch (e) {
      toast({ title: 'שגיאה', description: 'לא ניתן לעדכן מחירים', variant: 'destructive' });
    }
  };

  const handleAddSupplier = async (context) => {
    const name = window.prompt('שם הספק החדש:');
    if (!name || !name.trim()) return;
    try {
      const created = await addSupplier({ name: name.trim() });
      await loadSuppliers();
      if (context === 'new') handleNewRowChange('supplier', created.name);
      else setEditItem(prev => ({ ...prev, supplier: created.name }));
      toast({ title: 'ספק נוסף', description: created.name });
    } catch (e) {
      toast({ title: 'שגיאה', description: 'לא ניתן להוסיף ספק', variant: 'destructive' });
    }
  };


  const handleExport = (format) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/price-items/${workId}/export/${format}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" dir="rtl">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800">מחירון</h1>
            {workName && <p className="text-lg text-gray-600 mt-2">{workName}</p>}
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => handleExport('pdf')}
              variant="outline"
              className="border-2 border-red-500 text-red-600 hover:bg-red-50"
              data-testid="export-pdf-button"
            >
              <FileDown className="ml-2 w-5 h-5" />
              ייצוא PDF
            </Button>
            <Button
              onClick={() => handleExport('excel')}
              variant="outline"
              className="border-2 border-green-600 text-green-700 hover:bg-green-50"
              data-testid="export-excel-button"
            >
              <FileSpreadsheet className="ml-2 w-5 h-5" />
              ייצוא Excel
            </Button>
            <Button
              onClick={handleMergeDuplicates}
              variant="outline"
              className="border-2 border-purple-500 text-purple-600 hover:bg-purple-50"
              data-testid="merge-duplicates-button"
            >
              <Layers className="ml-2 w-5 h-5" />
              איחוד כפילויות
            </Button>
            <Button
              onMouseDown={handleRefreshMouseDown}
              onClick={handleRefresh}
              variant="outline"
              className="border-2 border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              <RefreshCw className="ml-2 w-5 h-5" />
              רענן
            </Button>
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="border-2"
            >
              <ArrowRight className="ml-2 w-5 h-5" />
              חזרה
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-6xl mx-auto">
          <Card className="shadow-xl">
            <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
              <CardTitle className="text-2xl">רשימת מחירים ({searchQuery ? `${filteredItems.length}/${items.length}` : items.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="p-4 border-b bg-white flex flex-wrap items-center justify-between gap-3">
                <div className="relative max-w-md flex-1 min-w-[220px]">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="חיפוש לפי שם חלק, ספק או חשבונית..."
                    className="pr-10 border-2 border-gray-300 focus:border-orange-500"
                    data-testid="pricelist-search"
                  />
                </div>
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="px-3 py-2 border-2 border-gray-300 rounded-lg bg-white text-sm focus:border-orange-500 min-w-[160px]"
                  data-testid="supplier-filter"
                >
                  <option value="">כל הספקים</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <span className="whitespace-nowrap">הדגש רווח מתחת ל:</span>
                  <div className="relative w-20">
                    <Input
                      value={lowProfitThreshold}
                      onChange={(e) => setLowProfitThreshold(parseFloat(e.target.value) || 0)}
                      type="number"
                      className="pl-5 border-2 border-gray-300 focus:border-red-400 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      style={{ MozAppearance: 'textfield' }}
                      data-testid="low-profit-threshold"
                    />
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600 whitespace-nowrap">עדכון מחירים גורף:</span>
                  <div className="relative w-28">
                    <Input
                      value={bulkPercent}
                      onChange={(e) => setBulkPercent(e.target.value)}
                      placeholder="אחוז"
                      type="number"
                      className="pl-6 border-2 border-gray-300 focus:border-green-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      style={{ MozAppearance: 'textfield' }}
                      data-testid="bulk-percent-input"
                    />
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                  </div>
                  <Button
                    onClick={handleBulkUpdate}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="bulk-update-button"
                  >
                    <Percent className="ml-1 w-4 h-4" />
                    עדכן
                  </Button>
                </div>
              </div>
              {isLoading ? (
                <div className="p-8 text-center text-gray-600">
                  <p className="text-lg">טוען...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th onClick={() => handleSort('partName')} className="px-4 py-4 text-right text-sm font-bold text-gray-700 cursor-pointer select-none hover:bg-gray-200"><SortIcon col="partName" />שם חלק</th>
                        <th onClick={() => handleSort('costPrice')} className="px-4 py-4 text-right text-sm font-bold text-gray-700 cursor-pointer select-none hover:bg-gray-200"><SortIcon col="costPrice" />מחיר עלות</th>
                        <th onClick={() => handleSort('price')} className="px-4 py-4 text-right text-sm font-bold text-gray-700 cursor-pointer select-none hover:bg-gray-200"><SortIcon col="price" />מחיר ללקוח</th>
                        <th onClick={() => handleSort('profit')} className="px-4 py-4 text-right text-sm font-bold text-gray-700 cursor-pointer select-none hover:bg-gray-200"><SortIcon col="profit" />רווח %</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">מספר חשבונית</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">תאריך</th>
                        <th onClick={() => handleSort('supplier')} className="px-4 py-4 text-right text-sm font-bold text-gray-700 cursor-pointer select-none hover:bg-gray-200"><SortIcon col="supplier" />שם ספק</th>
                        <th className="px-4 py-4 text-center text-sm font-bold text-gray-700">פעולות</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sortedItems.map((item) => {
                        const pp = profitPercent(item.price, item.costPrice);
                        const isLow = editingId !== item.id && pp != null && pp < lowProfitThreshold;
                        return (
                        <tr key={item.id} className={`hover:bg-gray-50 ${isLow ? 'bg-red-50' : ''}`} data-testid={isLow ? 'low-profit-row' : undefined}>
                          {editingId === item.id ? (
                            <>
                              <td className="px-4 py-4">
                                <Input
                                  value={editItem.partName}
                                  onChange={(e) => setEditItem({ ...editItem, partName: e.target.value })}
                                />
                              </td>
                              <td className="px-4 py-4">
                                <Input
                                  value={editItem.costPrice}
                                  onChange={(e) => setEditItem({ ...editItem, costPrice: e.target.value })}
                                  type="number"
                                  placeholder="עלות"
                                  className="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                  style={{ MozAppearance: 'textfield' }}
                                />
                              </td>
                              <td className="px-4 py-4">
                                <Input
                                  value={editItem.price}
                                  onChange={(e) => setEditItem({ ...editItem, price: e.target.value })}
                                  type="number"
                                  placeholder="מחיר ללקוח"
                                  className="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                  style={{ MozAppearance: 'textfield' }}
                                />
                              </td>
                              <td className="px-4 py-4 font-semibold text-green-700">
                                {profitPercent(editItem.price, editItem.costPrice) != null ? `${profitPercent(editItem.price, editItem.costPrice).toFixed(1)}%` : '-'}
                              </td>
                              <td className="px-4 py-4">
                                <select
                                  value={isSelectedInvoice(editItem.invoiceId) ? editItem.invoiceId : ''}
                                  onChange={(e) => { const inv = invoices.find(i => i.id === e.target.value); setEditItem({ ...editItem, invoiceId: e.target.value, supplier: inv && inv.supplierName ? inv.supplierName : editItem.supplier }); }}
                                  className="w-full px-2 py-2 border rounded-md mb-1"
                                >
                                  <option value="">בחר חשבונית</option>
                                  {invoices.map((inv) => (
                                    <option key={inv.id} value={inv.id}>
                                      {inv.invoiceNumber} - {inv.supplierName}
                                    </option>
                                  ))}
                                </select>
                                <Input
                                  value={isSelectedInvoice(editItem.invoiceId) ? '' : (editItem.invoiceId || '')}
                                  onChange={(e) => setEditItem({ ...editItem, invoiceId: e.target.value })}
                                  placeholder="או הקלד מס' חשבונית"
                                  className="text-sm"
                                  data-testid="edit-invoice-manual"
                                />
                              </td>
                              <td className="px-4 py-4">
                                <Input
                                  value={editItem.date || ''}
                                  onChange={(e) => setEditItem({ ...editItem, date: e.target.value })}
                                  type="date"
                                />
                              </td>
                              <td className="px-4 py-4">
                                <select
                                  value={editItem.supplier || ''}
                                  onChange={(e) => { if (e.target.value === '__add__') { handleAddSupplier('edit'); } else { setEditItem({ ...editItem, supplier: e.target.value }); } }}
                                  className="w-full px-2 py-2 border rounded-md"
                                  data-testid="edit-supplier"
                                >
                                  <option value="">בחר ספק</option>
                                  {suppliers.map((s) => (
                                    <option key={s.id} value={s.name}>{s.name}</option>
                                  ))}
                                  <option value="__add__">➕ הוסף ספק חדש...</option>
                                </select>
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex gap-2 justify-center">
                                  <Button onClick={() => handleUpdate(item.id)} size="sm" className="bg-green-500 hover:bg-green-600">
                                    <Save className="w-4 h-4" />
                                  </Button>
                                  <Button onClick={() => setEditingId(null)} size="sm" variant="outline">
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-4 text-gray-800 font-medium">{item.partName}</td>
                              <td className="px-4 py-4 text-orange-700">{item.costPrice ? `₪${item.costPrice}` : '-'}</td>
                              <td className="px-4 py-4 text-gray-800">₪{item.price}</td>
                              <td className="px-4 py-4 font-semibold text-green-700">
                                {profitPercent(item.price, item.costPrice) != null ? `${profitPercent(item.price, item.costPrice).toFixed(1)}%` : '-'}
                              </td>
                              <td className="px-4 py-4 text-gray-600">
                                {item.invoiceId ? (invoices.find(inv => inv.id === item.invoiceId)?.invoiceNumber || item.invoiceId) : '-'}
                              </td>
                              <td className="px-4 py-4 text-gray-600">
                                {item.date ? new Date(item.date).toLocaleDateString('he-IL') : '-'}
                              </td>
                              <td className="px-4 py-4 text-gray-700">{item.supplier || (invoices.find(inv => inv.id === item.invoiceId)?.supplierName) || '-'}</td>
                              <td className="px-4 py-4">
                                <div className="flex gap-2 justify-center">
                                  <Button onClick={() => handleEdit(item)} size="sm" variant="outline">
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button onClick={() => handleDuplicate(item)} size="sm" variant="outline" className="text-blue-600 hover:bg-blue-50" data-testid={`duplicate-${item.id}`}>
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                  <Button onClick={() => handleDelete(item.id)} size="sm" variant="outline" className="text-red-600 hover:bg-red-50">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                        );
                      })}
                      
                      {/* New Row - Always visible at bottom */}
                      <tr className="bg-green-50 hover:bg-green-100 transition-colors">
                        <td className="px-4 py-4">
                          <Input
                            value={newRow.partName}
                            onChange={(e) => handleNewRowChange('partName', e.target.value)}
                            placeholder="שם חלק..."
                            className="border-green-300 focus:border-green-500"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            value={newRow.costPrice}
                            onChange={(e) => handleNewRowChange('costPrice', e.target.value)}
                            placeholder="עלות..."
                            type="number"
                            className="border-green-300 focus:border-green-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            style={{ MozAppearance: 'textfield' }}
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            value={newRow.price}
                            onChange={(e) => handleNewRowChange('price', e.target.value)}
                            placeholder="מחיר ללקוח..."
                            type="number"
                            className="border-green-300 focus:border-green-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            style={{ MozAppearance: 'textfield' }}
                          />
                        </td>
                        <td className="px-4 py-4 font-semibold text-green-700">
                          {profitPercent(newRow.price, newRow.costPrice) != null ? `${profitPercent(newRow.price, newRow.costPrice).toFixed(1)}%` : '-'}
                        </td>
                        <td className="px-4 py-4">
                          <select
                            value={isSelectedInvoice(newRow.invoiceId) ? newRow.invoiceId : ''}
                            onChange={(e) => { const inv = invoices.find(i => i.id === e.target.value); handleNewRowChange('invoiceId', e.target.value); if (inv && inv.supplierName) handleNewRowChange('supplier', inv.supplierName); }}
                            className="w-full px-2 py-2 border border-green-300 rounded-md focus:border-green-500 mb-1"
                          >
                            <option value="">בחר חשבונית</option>
                            {invoices.map((inv) => (
                              <option key={inv.id} value={inv.id}>
                                {inv.invoiceNumber} - {inv.supplierName}
                              </option>
                            ))}
                          </select>
                          <Input
                            value={isSelectedInvoice(newRow.invoiceId) ? '' : (newRow.invoiceId || '')}
                            onChange={(e) => handleNewRowChange('invoiceId', e.target.value)}
                            placeholder="או הקלד מס' חשבונית"
                            className="border-green-300 text-sm"
                            data-testid="new-invoice-manual"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            value={newRow.date || ''}
                            onChange={(e) => handleNewRowChange('date', e.target.value)}
                            type="date"
                            className="border-green-300 focus:border-green-500"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <select
                            value={newRow.supplier || ''}
                            onChange={(e) => { if (e.target.value === '__add__') { handleAddSupplier('new'); } else { handleNewRowChange('supplier', e.target.value); } }}
                            onBlur={handleLastFieldBlur}
                            className="w-full px-2 py-2 border border-green-300 rounded-md focus:border-green-500"
                            data-testid="new-supplier"
                          >
                            <option value="">בחר ספק</option>
                            {suppliers.map((s) => (
                              <option key={s.id} value={s.name}>{s.name}</option>
                            ))}
                            <option value="__add__">➕ הוסף ספק חדש...</option>
                          </select>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <Button
                            onClick={handleSaveNewRow}
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            <Save className="w-4 h-4 ml-1" />
                            שמור
                          </Button>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      {(() => {
                        const cost = sortedItems.reduce((s, i) => s + (parseFloat(i.costPrice) || 0), 0);
                        const price = sortedItems.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);
                        const pv = sortedItems.map(i => profitPercent(i.price, i.costPrice)).filter(v => v != null);
                        const avg = pv.length ? pv.reduce((a, b) => a + b, 0) / pv.length : null;
                        return (
                          <tr className="bg-gray-100 font-bold border-t-2 border-gray-300" data-testid="pricelist-summary">
                            <td className="px-4 py-3 text-right text-gray-800">סה"כ {sortedItems.length} פריטים</td>
                            <td className="px-4 py-3 text-orange-700">₪{cost.toFixed(0)}</td>
                            <td className="px-4 py-3 text-gray-800">₪{price.toFixed(0)}</td>
                            <td className="px-4 py-3 text-green-700">{avg != null ? `${avg.toFixed(1)}%` : '-'}</td>
                            <td colSpan="4"></td>
                          </tr>
                        );
                      })()}
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PriceList;
